/**
 * 导出数据弹窗组件
 *
 * 功能：
 * - 提供数据导出功能的弹窗交互
 * - 显示导出数据条数
 * - 支持自定义导出API和参数
 * - 自动添加当前实体ID到请求参数
 *
 * @param {number} count - 导出数据条数，为0时导出按钮禁用
 * @param {string} apiUrl - 导出接口地址
 * @param {Record<string, any>} params - 导出接口额外参数
 * @param {string} buttonText - 导出按钮文本，默认为"导出"
 * @param {string} modalText - 弹窗标题文本，默认为"导出"
 * @param {() => void} onConfirm - 点击确认导出时的回调函数
 *
 */
import { useState } from 'react'
import { Modal, Button } from 'antd'
import request from '@/utils/request'
import { useAppSelector } from '@/utils/hooks'

interface ExportModalProps {
  onConfirm?: () => void
  count: number
  apiUrl?: string
  params?: Record<string, any>
  buttonText?: string
  modalText?: string
  selectedRowKeys?: number[]
}

const ExportModal: React.FC<ExportModalProps> = ({
  onConfirm,
  count = 0,
  apiUrl,
  params = {},
  buttonText = '导出',
  modalText = '导出',
  selectedRowKeys = [],
}) => {
  const [app] = useAppSelector(state => [state.app])
  const [exportVisible, setExportVisible] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleConfirm = () => {
    if (!apiUrl) return
    setLoading(true)
    onConfirm && onConfirm()
    request(apiUrl, {
      data: {
        entityId: app?.currentEntity?.entityId,
        ...params,
        ids: selectedRowKeys,
      },
    })
      .then(() => {
        onCancel()
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const onCancel = () => {
    setExportVisible(false)
  }

  const exportTotalTips = selectedRowKeys.length
    ? `已选择 ${selectedRowKeys.length} 条记录`
    : `检测到查询结果有 ${count} 条记录。`

  return (
    <>
      <Button
        type="primary"
        disabled={!count}
        onClick={() => {
          setExportVisible(true)
        }}
      >
        {buttonText}
      </Button>
      <Modal
        title={modalText}
        open={exportVisible}
        onCancel={onCancel}
        onOk={handleConfirm}
        destroyOnClose
        maskClosable={false}
        confirmLoading={loading}
      >
        <div>
          <p>{exportTotalTips}</p>
          <p>可前往【导出记录】页面查看进度并下载数据。</p>
        </div>
      </Modal>
    </>
  )
}

export { ExportModal }
export default ExportModal
